import { createSlice } from "@reduxjs/toolkit";
import {
  savedWordFields,
  operands,
} from "../../../page elements/Mapping Alias/Constructor/enums";
import { flatMap, findKey } from "lodash";

const initialState = {
  savedRules: [],
  initialRules: [],
  selectedWords: {},
  currentID: null,
  isEditorCalled: false,
  isLLMChatCalled: false,
  llmGeneratedRules: [],
  llmUserInput: "",
};

const transformRules = (keywordsList) => {
  return keywordsList?.rules?.map((item) => ({
    ...item,
    ruleID: self.crypto.randomUUID(),
  }));
};

const AliasRulesConstructorSlicer = createSlice({
  name: "aliasRulesConstructor",
  initialState,
  reducers: {
    setWord: (state, action) => {
      const { label, value, forElement } = action.payload;
      if (typeof label !== "string" || typeof value !== "string") {
        return;
      }
      state.selectedWords ??= {};
      state.selectedWords[forElement] = { label, value };
    },
    setNewWord: (state, action) => {
      const { label, value, forElement } = action.payload;
      if (typeof label !== "string" || typeof value !== "string") {
        return;
      }

      state.selectedWords ??= {};
      state.selectedWords[forElement] ??= [];
      state.selectedWords[forElement].push({ label, value });
    },
    setCustomInput: (state, action) => {
      const { input, forElement, isSingle } = action.payload;
      if (typeof input !== "string") return;

      state.selectedWords ??= {};

      if (isSingle) {
        state.selectedWords[forElement] = { label: input, value: input };
        return;
      }

      state.selectedWords[forElement] ??= [];
      const splitWords = input
        .split(/[,;]\s*/)
        .map((word) => word.trim())
        .filter((word) => word !== "");

      const tempResults = [];

      splitWords.forEach((word) =>
        tempResults.push({ label: word, value: word })
      );
      state.selectedWords[forElement] = tempResults;
    },
    openEditField: (state, action) => {
      const dataToSave = action.payload;

      if (typeof dataToSave !== "object") return;

      state.selectedWords ??= {};

      const flatOperands = flatMap(operands, (value) =>
        typeof value === "object" ? Object.entries(value) : [[value, value]]
      );

      const operandMap = Object.fromEntries(flatOperands);

      for (const [key, value] of Object.entries(dataToSave)) {
        const forElement = savedWordFields[key];
        if (!forElement) continue;

        state.selectedWords[forElement] ??= [];

        if (Array.isArray(value)) {
          const formattedArray = value.map((el) => ({
            label: operandMap[el] || el,
            value: el,
          }));
          state.selectedWords[forElement] = formattedArray;
        } else {
          const label = operandMap[value] || value;
          state.selectedWords[forElement] = { label, value };
        }
      }
    },
    deleteWord: (state, action) => {
      const { index: deletedWordIndex, forElement } = action.payload;
      if (typeof deletedWordIndex !== "number") return;
      state.selectedWords[forElement] = state.selectedWords[forElement].filter(
        (_, i) => i !== deletedWordIndex
      );
    },
    setSavedRules: (state, action) => {
      const { keywordsList } = action.payload;
      if (typeof keywordsList !== "object") return;

      const updatedRules = transformRules(keywordsList);

      state.savedRules = updatedRules;
      state.initialRules = updatedRules;
      state.currentID = keywordsList.id;
    },
    updateSavedRule: (state, action) => {
      const newRulesArray = action.payload;
      if (!Array.isArray(newRulesArray)) return;
      state.savedRules = newRulesArray;
    },
    clearSelectedWords: (state) => {
      state.selectedWords = {};
    },
    clearConstructorSlicer: (state) => {
      state.selectedWords = {};
      state.initialRules = [];
      state.savedRules = [];
      state.currentID = null;
    },
    changeEditorState: (state, action) => {
      const newState = action.payload;
      if (typeof newState == "boolean") state.isEditorCalled = newState;
      else state.isEditorCalled = !state.isEditorCalled;
    },
    changeLLMState: (state, action) => {
      const newState = action.payload;
      if (typeof newState == "boolean") state.isLLMChatCalled = newState;
      else state.isLLMChatCalled = !state.isLLMChatCalled;
    },
    setLLMGeneratedRules: (state, action) => {
      const llmGeneratedRules = action.payload;
      if (typeof llmGeneratedRules !== "object") return;
      const updatedRules = transformRules(llmGeneratedRules);
      state.llmGeneratedRules = updatedRules;
    },
    acceptLLMGeneratedRule: (state, action) => {
      const ruleID = action.payload;
      console.log(ruleID);

      const rule = state.llmGeneratedRules.find((r) => r.ruleID === ruleID);

      if (!rule) return;

      const updatedRule = { ...rule };

      for (const [key, value] of Object.entries(state.selectedWords)) {
        let formattedValue;
        if (Array.isArray(value))
          formattedValue = value.map((obj) => obj?.value);
        else if (typeof value === "object") formattedValue = value?.value;
        else formattedValue = value;

        const keyToUpdate = findKey(savedWordFields, (val) => val === key);
        if (keyToUpdate) updatedRule[keyToUpdate] = formattedValue;
      }

      state.savedRules.push(updatedRule);
      state.llmGeneratedRules = state.llmGeneratedRules.filter(
        (r) => r.ruleID !== ruleID
      );
    },

    rejectLLMGeneratedRule: (state, action) => {
      const ruleID = action.payload;
      state.llmGeneratedRules = state.llmGeneratedRules.filter(
        (r) => r.ruleID !== ruleID
      );
    },

    setUserInput: (state, action) => {
      const userInput = action.payload;
      if (typeof userInput !== "string") return;

      state.llmUserInput = userInput;
    },
  },
});

export const {
  setWord,
  setNewWord,
  openEditField,
  deleteWord,
  setSavedRules,
  setCustomInput,
  updateSavedRule,
  clearSelectedWords,
  clearConstructorSlicer,
  changeEditorState,
  changeLLMState,
  setLLMGeneratedRules,
  acceptLLMGeneratedRule,
  rejectLLMGeneratedRule,
  setUserInput,
} = AliasRulesConstructorSlicer.actions;

export default AliasRulesConstructorSlicer.reducer;
